import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useSession } from './session-store'
import { callNative, useBridgeEvent } from '@/bridge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/** Blocks rendering until native hands over the session (token, org, user). */
export function SessionGate({ children }: { children: ReactNode }) {
  const session = useSession((s) => s.session)
  const setSession = useSession((s) => s.setSession)

  const { error, refetch, isFetching } = useQuery({
    queryKey: ['bridge', 'session'],
    queryFn: async () => {
      const s = await callNative('getSession')
      setSession(s)
      return s
    },
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useBridgeEvent('sessionChanged', setSession)

  if (session) return children

  if (error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-muted-foreground text-sm">Couldn't start: {error.message}</p>
        <Button onClick={() => refetch()} disabled={isFetching}>
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3 p-4 pt-[calc(env(safe-area-inset-top)+1rem)]">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  )
}
